import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { REACT_APP_GLOBAL_API_BASE_URL_V2 } from 'runtimeEnvVars';

import {
  clearApplicantCache,
  updateApplicant,
  updateApplicantDataFieldColumn,
} from 'containers/ApplicantTableV2/actions';
import {
  addErrorMessageFromCaughtValueI18n,
  addMessageAction,
} from 'containers/FlashMessage/actions';
import { apiPut } from 'utils/axios';

/**
 * @param {string | null | undefined} applicantId — nullish at some call sites
 *   (DataValidationModal and its fields), which interpolates into a URL the
 *   server rejects. Annotated as it is actually called rather than as it ought to
 *   be: tightening this to `string` is a real fix for those callers, but it
 *   belongs with their null-handling and not in this endpoint's change.
 * @param {string | undefined} dataType — `info` sub-resource: data,
 *   structured_data, secure_data, review_questionnaire. Optional at the same call
 *   sites and for the same reason as applicantId.
 * @param {boolean} preventStoreUpdates
 * @param {{ disableMessage?: boolean, disableErrorMessage?: boolean }} options
 *   `disableErrorMessage` suppresses the error toast for callers that render the
 *   failure inline next to the field instead.
 */
export default function useApplicantUpdateDetail(
  applicantId,
  dataType,
  preventStoreUpdates = false,
  options = {},
) {
  const dispatch = useDispatch();
  const intl = useIntl();

  const url = `${REACT_APP_GLOBAL_API_BASE_URL_V2}/applicants/${applicantId}/info/${dataType}`;

  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState('');

  const dispatchUpdateSelectedApplicant = data => {
    if (!preventStoreUpdates) {
      dispatch(updateApplicant(applicantId, dataType, data));
    }
  };

  const dispatchUpdateApplicantDataFieldColumn = dataField => {
    dispatch(updateApplicantDataFieldColumn(applicantId, dataField));
  };

  /**
   * @returns {Promise<{ ok: boolean, errorMessage: string | null }>} `ok: false`
   *   only when the request itself was rejected, so callers can keep an editor
   *   open on the value the server refused.
   */
  const updateApplicantDetail = async (
    params,
    successMessage,
    dataField = {},
  ) => {
    setIsFetching(true);
    try {
      const res = await apiPut(url, params);

      if (!res || !res.data) {
        return { ok: true, errorMessage: null };
      }

      if (!options.disableMessage) {
        dispatch(addMessageAction(successMessage, 'success'));
      }
      const data = res.data[dataType];
      dispatchUpdateSelectedApplicant(data);
      dispatchUpdateApplicantDataFieldColumn(dataField);
      setError('');
      return { ok: true, errorMessage: null };
    } catch (e) {
      setError(e);
      // The axios interceptor resolves the response body — including a Rails
      // field-error hash such as the review questionnaire's pay-rate bound
      // messages — down to one user-facing string, so report that rather than
      // the generic "something went wrong". Build the action either way: its
      // text is also what an inline caller renders next to the field.
      const errorAction = addErrorMessageFromCaughtValueI18n(e, intl);
      if (!options.disableErrorMessage) {
        dispatch(errorAction);
      }
      return { ok: false, errorMessage: errorAction.text };
    } finally {
      setIsFetching(false);
      dispatch(clearApplicantCache(applicantId));
    }
  };

  return { updateApplicantDetail, isFetching, error };
}
